import React, { useState } from 'react'
import './styles/notification-builder-preview.scss'
import { Button, Well } from '@pushly/aqe/lib/components'
import { getClassNames } from '../../../_utils/classnames'
import { extractVariantPreview } from '../helpers'
import { DomainDto } from '../../../dtos/domain'
import { UserDto } from '../../../dtos/user'
import PreviewSDK, { Platform } from '@pushly/cuttlefish'
import { InfoCircleOutlined, WarningOutlined } from '@ant-design/icons'
import { Popover } from 'antd'
import { getPlatformNotifications } from '../../../_utils/platform'
import { NotificationVariantModel } from '../../../models/notification/notification-variant.model'
import { NotificationBuilderSubmitType } from '../enums'
import { getPushlySdk } from '../../../_utils/pushly-sdk'
import { pause, simpleNotification } from '../../../_utils/utils'
import { UserService } from '../../../services'
import { Container } from 'typescript-ioc/es5'
import { useService } from '../../../hooks/use-service'
import { AppState } from '../../../stores/app'
import { AbilityAction } from '../../../enums/ability-action.enum'
import { SubjectEntity } from '../../../enums/ability-entity.enum'
import { BetterSelect } from '../../better-select/better-select'
import { IBetterSelectOption } from '../../better-select/interfaces'
import { usePersistedNotificationPreviewState } from '../../../stores/notification-preview-state'
import { observer } from 'mobx-react'
import { coalesce } from '../../../_utils/coalesce'
import { DeliveryChannel, Macro, strIsKnownMacro } from '@pushly/models/lib/enums'
import { useUserPreferences } from '@pushly/aqe/lib/providers/user-preferences-provider/core/index'
import { PreviewPlatform } from '@pushly/models/lib/enums/preview-platform'
import { isDeliveryChannelEnabled } from '../../../_utils/domain'
import { FEAT_CHANNEL_ANDROID, FEAT_CHANNEL_IOS } from '../../../constants'

interface INotificationBuilderPreview {
    level: 'org' | 'domain'
    levelId: number
    domain: DomainDto
    source: NotificationVariantModel
    options?: IBetterSelectOption[]
    loading?: boolean
    hideInlinePreview?: boolean
    hideDevicePreview?: boolean
    chromeless?: boolean
    onPreviewRequest?: (type: NotificationBuilderSubmitType) => void
}

const WEB_PLATFORMS = [PreviewPlatform.ANDROID, PreviewPlatform.MAC_OS, PreviewPlatform.WINDOWS]

async function registerUserPuuid(user: UserDto, domain: DomainDto): Promise<void> {
    try {
        const sdk = getPushlySdk()
        if (!sdk) {
            simpleNotification('error', 'We are unable to send you a preview notification at this time.')
            return
        }

        const sdkUser = sdk.context.user

        const currentRegistrations: any[] = user.puuids || []

        const permission = await new Promise<string>(async (res) => {
            // this is referring to system prompts / cannot use PromptStyle enum due to SDK style implementation
            if (!sdkUser?.isSubscribed) {
                sdk.on('permission_allowed', async () => {
                    // Allow 3 secs for ES doc to be updated
                    await pause(3000)
                    res('subscribed')
                })

                sdk.on('permission_dismissed', () => res('dismissed'))
                sdk.on('permission_denied', () => res('denied'))
                sdk.PushNotifications.showPermissionPrompt()
            } else {
                res('subscribed')
            }
        })

        if (permission === 'subscribed') {
            const userSvc: UserService = Container.get(UserService)

            const existingRegistration = currentRegistrations.find(
                (r) => r.puuid === sdkUser.puuid && r.domainId === domain.id,
            )

            if (!existingRegistration) {
                await userSvc.registerPuuid(user.id, domain.id, sdkUser.puuid)
            }
        } else if (permission === 'denied') {
            simpleNotification(
                'error',
                `We are unable to send you a preview 
                    of this notification because you have denied notification 
                    permissions. Please manually reset your browser permissions 
                    and attempt to send the preview again.`,
            )
        }
    } catch (_) {}
}

export enum PreviewState {
    COLLAPSED = 'collapsed',
    EXPANDED = 'expanded',
}

const NotificationBuilderPreview = observer((props: INotificationBuilderPreview) => {
    const appState = useService<AppState>(AppState)

    const isDomainLevel = props.level === 'domain'
    const userPreferences = useUserPreferences(
        appState.currentUser?.id,
        isDomainLevel ? props.levelId : undefined,
        !isDomainLevel ? props.levelId : undefined,
    )
    const notificationPreviewState = usePersistedNotificationPreviewState()

    const { currentUser, currentDomain } = appState
    const { domain, source, options, loading, hideInlinePreview, hideDevicePreview, chromeless, onPreviewRequest } =
        props

    const npsCache = notificationPreviewState.value.getCache(props.level, props.levelId)
    const userNpsPreferences = userPreferences.get(['notification_preview_state'])
    const PREVIEW_BATCH_KEY = 'preview-settings'

    const setSelectedPreviewPlatform = (value: PreviewPlatform) => {
        if (userNpsPreferences) {
            setPreviewPlatform(value)
            userPreferences.setDebounced(['notification_preview_state', 'platform'], value, {
                batchKey: PREVIEW_BATCH_KEY,
                delay: 1000,
            })
        } else if (value && npsCache.platform !== value) {
            notificationPreviewState.value.isReady.then((nps) => {
                npsCache.platform = value
            })
        }
    }

    const setSelectedPreviewState = (value: PreviewState) => {
        if (userNpsPreferences) {
            setPreviewState(value)
            userPreferences.setDebounced(['notification_preview_state', 'state'], value, {
                batchKey: PREVIEW_BATCH_KEY,
                delay: 1000,
            })
        } else if (value && npsCache.state !== value) {
            notificationPreviewState.value.isReady.then((nps) => {
                npsCache.state = value
            })
        }
    }

    let userSubStatus = 'default'
    const platformNotifications = getPlatformNotifications()
    const sdk = getPushlySdk()
    if (sdk) {
        userSubStatus = sdk.context?.user?.subscriberState
    }

    const canSendPreview =
        platformNotifications.supported &&
        appState.abilityStore.can(
            AbilityAction.CREATE,
            appState.abilityStore.getDomainOwnedIdentityFor(SubjectEntity.NOTIFICATION),
        )

    const SubStatusAllowList = ['default', 'subscribed', 'dismissed']
    const dismissPreviewAction = userSubStatus === 'dismissed'
    const disablePreviewAction = SubStatusAllowList.indexOf(userSubStatus) === -1

    const domainIsWebEnabled = isDeliveryChannelEnabled(domain, DeliveryChannel.WEB)
    const domainHasNativeIOS = domain.flags.includes(FEAT_CHANNEL_IOS)
    const domainHasNativeAndroid = domain.flags.includes(FEAT_CHANNEL_ANDROID)

    // Build default preview platform options based on domain capabilities
    const defaultOptions: { value: PreviewPlatform | Platform; label: string }[] = []
    if (domainIsWebEnabled) {
        defaultOptions.push({
            value: Platform.ANDROID,
            label: 'Android',
        })
    }
    if (domainHasNativeAndroid) {
        defaultOptions.push({
            value: Platform.NATIVE_ANDROID,
            label: 'Android (Native)',
        })
    }
    if (domainHasNativeIOS) {
        defaultOptions.push({
            value: Platform.IOS,
            label: 'Apple / iOS',
        })
    }
    if (domainIsWebEnabled) {
        defaultOptions.push({
            value: Platform.MAC_OS,
            label: 'MacOS',
        })
    }
    if (domainIsWebEnabled) {
        defaultOptions.push({
            value: Platform.WINDOWS,
            label: 'Web / Windows',
        })
    }

    // Handle a scenario where the default (global) platform is a web-enabled platform
    // but the domain does not support web notifications.
    let defaultSelectedPreviewPlatform = userNpsPreferences?.platform
    if (!domainIsWebEnabled && WEB_PLATFORMS.includes(defaultSelectedPreviewPlatform)) {
        if (domainHasNativeAndroid) {
            defaultSelectedPreviewPlatform = PreviewPlatform.NATIVE_ANDROID
        } else if (domainHasNativeIOS) {
            defaultSelectedPreviewPlatform = PreviewPlatform.IOS
        }
    }

    const [previewPlatform, setPreviewPlatform] = useState<PreviewPlatform>(
        npsCache.resolvePlatformFromAvailableChannels(source.getChannels(), defaultSelectedPreviewPlatform),
    )
    const [previewState, setPreviewState] = useState<PreviewState>(
        userNpsPreferences?.state ?? coalesce(npsCache.state, PreviewState.COLLAPSED),
    )

    const activePreviewConfig = extractVariantPreview(domain, source, previewPlatform, previewState, false)
    const selectedVariantImageUrl = source?.getContent()?.getDefaultContent()?.getImageUrl() ?? ''

    return !notificationPreviewState.isLoaded ? null : (
        <>
            <Well
                className={getClassNames('notification-builder-preview', 'nested', {
                    'no-inline-preview': hideInlinePreview,
                    'no-device-preview': hideDevicePreview,
                    'has-ecomm-image-macro': strIsKnownMacro(selectedVariantImageUrl, {
                        exact: true,
                        allowedMacros: [Macro.ECOMM_ITEM_IMAGE],
                    }),
                    'has-recommendation-image-macro': strIsKnownMacro(selectedVariantImageUrl, {
                        exact: true,
                        allowedMacros: [Macro.ARTICLE_IMAGE],
                    }),
                })}
                disabled={loading}
                hideFooter={true}
                mode={chromeless ? 'ghost' : 'default'}
                title={
                    <span>
                        <span>Preview</span>
                        <Popover
                            overlayClassName={getClassNames('notification-builder-popover', 'inline-preview-popover')}
                            content={
                                <>
                                    <p>
                                        The platform preview is a best-guess at how this notification will look across
                                        the most common devices for the selected operating system.
                                    </p>

                                    <p>
                                        Due to variability between devices this preview may not accurately depict title
                                        & body length limitations or image crop & placement. A more accurate preview may
                                        be obtained via a notification preview on your browser or mobile device by using
                                        the Device Preview.
                                    </p>
                                </>
                            }
                        >
                            <InfoCircleOutlined className="info-icon" />
                        </Popover>
                    </span>
                }
            >
                <div className="preview-select">
                    <BetterSelect
                        prefix="Platform"
                        disableSearch={true}
                        value={[previewPlatform]}
                        onChange={(val: PreviewPlatform | Platform) => {
                            switch (val) {
                                case Platform.MAC_OS:
                                case PreviewPlatform.MAC_OS:
                                    setSelectedPreviewState(PreviewState.COLLAPSED)
                                    break
                                case Platform.ANDROID:
                                case Platform.NATIVE_ANDROID:
                                case PreviewPlatform.ANDROID:
                                case PreviewPlatform.NATIVE_ANDROID:
                                    setSelectedPreviewState(PreviewState.EXPANDED)
                                    break
                            }
                            if (val && Object.values(PreviewPlatform).includes(val as PreviewPlatform)) {
                                setSelectedPreviewPlatform(val as PreviewPlatform)
                            }
                        }}
                        options={options?.length ? options : defaultOptions}
                    />

                    <BetterSelect
                        prefix="State"
                        disableSearch={true}
                        value={[previewState]}
                        onChange={(v: PreviewState) => {
                            setSelectedPreviewState(v)
                        }}
                        options={[
                            {
                                value: PreviewState.EXPANDED,
                                label: 'Expanded',
                            },
                            {
                                value: PreviewState.COLLAPSED,
                                label: 'Collapsed',
                            },
                        ]}
                    />
                </div>

                {!hideInlinePreview && (
                    <div className="preview-wrapper-container" data-image-url={selectedVariantImageUrl}>
                        <Well
                            className="preview-wrapper"
                            disabled={loading}
                            hideHeader={true}
                            hideFooter={true}
                            mode="ghost"
                        >
                            <PreviewSDK.Preview config={activePreviewConfig} />
                        </Well>
                    </div>
                )}

                {!hideDevicePreview && (
                    <Well
                        className={getClassNames('notification-builder-device-preview', {
                            unsupported: !platformNotifications.supported,
                        })}
                        disabled={loading}
                        hideFooter={true}
                        mode="ghost"
                        title={
                            <span>
                                Device Preview
                                {!platformNotifications.supported ? (
                                    <WarningOutlined className="info-icon" />
                                ) : (
                                    <Popover
                                        overlayClassName={getClassNames(
                                            'notification-builder-popover',
                                            'device-preview-popover',
                                        )}
                                        content={
                                            <>
                                                <p>
                                                    A device preview will immediately deliver the selected notification
                                                    to all registered devices for the chosen preview destination.
                                                </p>
                                                <p>
                                                    You may choose to deliver a preview of the currently selected
                                                    notification to your registered personal devices (Personal Preview)
                                                    or to all internal <span>{domain.displayName} </span>
                                                    users that have registered devices for preview notifications (Team
                                                    Preview).
                                                </p>

                                                {platformNotifications.supported ? (
                                                    !dismissPreviewAction ? (
                                                        !disablePreviewAction ? (
                                                            <>
                                                                <p>
                                                                    To receive previews on multiple devices you may
                                                                    <br />
                                                                    <a
                                                                        className="register-preview-device-link"
                                                                        href={`${appState.documentationLink}/platform/notifications/notification-previews#registering-additional-preview-devices`}
                                                                        target="_blank"
                                                                    >
                                                                        associate additional devices
                                                                    </a>{' '}
                                                                    with your user.
                                                                </p>

                                                                <p>
                                                                    Any additional devices you add will be added to your
                                                                    list of personal devices and will receive a
                                                                    notification preview when you choose the 'Personal
                                                                    Preview' destination or someone else sends a 'Team
                                                                    Preview'.
                                                                </p>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <p>Notifications are blocked in the current browser.</p>
                                                                <p>
                                                                    Please manually reset your browser permissions,
                                                                    reload the page, and re-register this device for
                                                                    notification previews.
                                                                </p>
                                                            </>
                                                        )
                                                    ) : (
                                                        <>
                                                            <p>
                                                                Notifications have been dismissed in the current
                                                                browser.
                                                            </p>
                                                            <p>
                                                                Please reload the page, and re-register this device for
                                                                notification previews.
                                                            </p>
                                                        </>
                                                    )
                                                ) : (
                                                    <p>Notifications are not supported in the current browser.</p>
                                                )}
                                            </>
                                        }
                                    >
                                        <InfoCircleOutlined className="info-icon" />
                                    </Popover>
                                )}
                            </span>
                        }
                        actions={
                            canSendPreview && (
                                <div>
                                    <Button
                                        size="small"
                                        onClick={async () => {
                                            await registerUserPuuid(currentUser!, currentDomain!)
                                            return onPreviewRequest?.(NotificationBuilderSubmitType.PERSONAL_PREVIEW)
                                        }}
                                        disabled={false}
                                        actions={[
                                            {
                                                text: 'Send Team Preview',
                                                onClick: async () => {
                                                    await registerUserPuuid(currentUser!, currentDomain!)
                                                    return onPreviewRequest?.(
                                                        NotificationBuilderSubmitType.TEAM_PREVIEW,
                                                    )
                                                },
                                                disabled: false,
                                                disabledMessage:
                                                    'At least one team member must be registered to send a team preview.',
                                            },
                                        ]}
                                    >
                                        <span>Send Personal Preview</span>
                                    </Button>
                                </div>
                            )
                        }
                    >
                        {!platformNotifications.supported && (
                            <p>
                                Device previews are currently unavailable. Push Notifications are not supported in the
                                current browser session. Please ensure you are not running in incognito or private mode
                                and that you are not running any ad-blockers.
                            </p>
                        )}
                    </Well>
                )}
            </Well>
        </>
    )
})

export default NotificationBuilderPreview
